import React, { useCallback, useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer, View } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { IonButton, IonSegment, IonSegmentButton, IonLabel } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import { IonIcon } from "@ionic/react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { CalendarJobEvent, getEventStyle } from "../../utils/calendarUtils";

// ─── Stacked day header (number + abbreviation) ───────────────────────────────

const StackedDayHeader: React.FC<{ date: Date; label: string }> = ({ date, label: _label }) => {
  const isToday = new Date().toDateString() === date.toDateString();
  return (
    <div style={{ textAlign: "center", lineHeight: 1.2, padding: "2px 0" }}>
      <div style={{
        fontSize: 15,
        fontWeight: isToday ? 700 : 400,
        color: isToday ? "var(--ion-color-primary)" : undefined,
      }}>
        {format(date, "dd")}
      </div>
      <div style={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: isToday ? "var(--ion-color-primary)" : "var(--ion-color-medium)",
      }}>
        {format(date, "EEE")}
      </div>
    </div>
  );
};

// ─── date-fns localizer ───────────────────────────────────────────────────────

const locales = { "en-US": enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

// ─── Props ────────────────────────────────────────────────────────────────────

interface CalendarViewProps {
  events: CalendarJobEvent[];
  onSelectEvent: (resource: CalendarJobEvent["resource"]) => void;
  onClearSelection?: () => void;
  isScheduler: boolean;
  selectedDocId?: string | null;
  onLabelChange?: (label: string) => void;
  showLabelInToolbar?: boolean;
  onVisibleDateChange?: (date: Date) => void;
}

// ─── Status-colour legend ─────────────────────────────────────────────────────

const Legend: React.FC<{ isScheduler: boolean }> = ({ isScheduler }) => {
  const items = isScheduler
    ? [
        { color: "var(--ion-color-warning)", label: "Open" },
        { color: "var(--ion-color-secondwarning)", label: "Filled" },
        { color: "var(--ion-color-medium)", label: "Past" },
        { color: "var(--ion-color-danger)", label: "Cancelled" },
      ]
    : [
        { color: "var(--ion-color-primary)", label: "Invited" },
        { color: "var(--ion-color-success)", label: "Scheduled" },
        { color: "var(--ion-color-medium)", label: "Past" },
        { color: "var(--ion-color-danger)", label: "Cancelled" },
      ];

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        padding: "6px 12px calc(6px + env(safe-area-inset-bottom)) 12px",
        borderTop: "1px solid var(--ion-color-light-shade)",
      }}
    >
      {items.map((it) => (
        <div
          key={it.label}
          style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}
        >
          <div
            style={{
              width: 5,
              height: 14,
              borderRadius: 2,
              backgroundColor: it.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: "var(--ion-color-medium-shade)" }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const CalendarToolbar: React.FC<{
  label: string;
  view: View;
  onView: (v: View) => void;
  onNavigate: (action: "PREV" | "NEXT" | "TODAY") => void;
  isScheduler: boolean;
  onLabelChange?: (label: string) => void;
  showLabel?: boolean;
}> = ({ label, view, onView, onNavigate, isScheduler, onLabelChange, showLabel = true }) => {
  const roleColor = isScheduler ? "secondwarning" : "warning";

  useEffect(() => {
    onLabelChange?.(label);
  }, [label, onLabelChange]);

  return (
    <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 12px",
      borderBottom: "1px solid var(--ion-color-light-shade)",
      flexShrink: 0,
    }}
  >
    {/* Navigation */}
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <IonButton
        fill="clear"
        size="small"
        color={roleColor}
        onClick={() => onNavigate("PREV")}
        style={{ margin: 0 }}
      >
        <IonIcon
          icon={chevronBackOutline}
          slot="icon-only"
        />
      </IonButton>

      <IonButton
        fill="outline"
        size="small"
        color={roleColor}
        onClick={() => onNavigate("TODAY")}
        style={{ margin: 0, fontWeight: 600 }}
      >
        Today
      </IonButton>

      <IonButton
        fill="clear"
        size="small"
        color={roleColor}
        onClick={() => onNavigate("NEXT")}
        style={{ margin: 0 }}
      >
        <IonIcon
          icon={chevronForwardOutline}
          slot="icon-only"
        />
      </IonButton>
    </div>

    {/* Current range label */}
    {showLabel && <strong style={{ fontSize: 14 }}>{label}</strong>}

    {/* View toggle */}
    <IonSegment
      value={view}
      onIonChange={(e: any) => onView(e.detail.value as View)}
      color={roleColor}
      style={{ maxWidth: 160 }}
    >
      <IonSegmentButton
        value="week"
        style={{ minWidth: 60 }}
      >
        <IonLabel
        >
          Week
        </IonLabel>
      </IonSegmentButton>
      <IonSegmentButton
        value="month"
        style={{ minWidth: 60 }}
      >
        <IonLabel
        >
          Month
        </IonLabel>
      </IonSegmentButton>
    </IonSegment>
  </div>
  );
};

const CalendarView: React.FC<CalendarViewProps> = ({ events, onSelectEvent, onClearSelection, isScheduler, selectedDocId, onLabelChange, showLabelInToolbar = true, onVisibleDateChange }) => {
  const [currentView, setCurrentView] = useState<View>("week");
  const [currentDate, setCurrentDate] = useState(new Date());

  // Lets the parent page paginate in older data as the visible range moves back
  useEffect(() => {
    onVisibleDateChange?.(currentDate);
  }, [currentDate, onVisibleDateChange]);

  const handleNavigate = useCallback((action: "PREV" | "NEXT" | "TODAY") => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (action === "TODAY") return new Date();

      if (currentView === "week") {
        d.setDate(d.getDate() + (action === "NEXT" ? 7 : -7));
      } else {
        d.setMonth(d.getMonth() + (action === "NEXT" ? 1 : -1));
      }
      return d;
    });
  }, [currentView]);

  const eventStyleGetter = useCallback(
    (event: CalendarJobEvent) => {
      const base = getEventStyle(event);
      const isSelected = !!selectedDocId && event.resource.firestoreDocId === selectedDocId;
      const hasSelection = !!selectedDocId;

      if (isSelected) {
        return {
          style: {
            ...base.style,
            outline: "4px solid #fff",
            outlineOffset: "-4px",
            boxShadow: "0 0 0 2px #111, 0 0 0 6px rgba(255,255,255,0.95), 0 0 14px 5px rgba(255,255,255,0.6)",
            zIndex: 20,
            transform: "scale(1.04)",
          },
        };
      }

      if (hasSelection) {
        // Dim every other event so the selected one stands out
        return {
          style: {
            ...base.style,
            opacity: typeof base.style?.opacity === "number" ? Math.max(base.style.opacity * 0.75, 0.65) : 0.7,
          },
        };
      }

      return base;
    },
    [selectedDocId]
  );

  const handleSelectEvent = useCallback(
    (event: CalendarJobEvent) => onSelectEvent(event.resource),
    [onSelectEvent]
  );

  const handleSelectSlot = useCallback(() => {
    if (onClearSelection) {
      onClearSelection();
    }
  }, [onClearSelection]);

  // Custom toolbar component — receives props from react-big-calendar
  const CustomToolbar = useCallback(
    (tbProps: any) => (
      <CalendarToolbar
        label={tbProps.label}
        view={currentView}
        onView={(v) => { setCurrentView(v); tbProps.onView(v); }}
        onNavigate={(action) => { handleNavigate(action); tbProps.onNavigate(action); }}
        isScheduler={isScheduler}
        onLabelChange={onLabelChange}
        showLabel={showLabelInToolbar}
      />
    ),
    [currentView, handleNavigate, isScheduler, onLabelChange, showLabelInToolbar]
  );

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Calendar
          localizer={localizer}
          events={events}
          onSelectSlot={handleSelectSlot}
          selectable
          date={currentDate}
          view={currentView}
          views={["week", "month"]}
          onView={setCurrentView}
          onNavigate={setCurrentDate}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          components={{ toolbar: CustomToolbar, header: StackedDayHeader }}
          formats={{ timeGutterFormat: "HH:mm" }}
          style={{ height: "100%" }}
          popup
          tooltipAccessor={(event: CalendarJobEvent) =>
            [event.resource.company, event.resource.shiftIdentifier]
              .filter(Boolean)
              .join(" · ")}
        />
      </div>
      <Legend isScheduler={isScheduler} />
    </div>
  );
};

export default CalendarView;
